/**
 * The AI bulb in an editor, from the caller's side: send the form's values, get the same task or
 * note back updated, or an error a person can act on.
 *
 * Shared by the editors that wear the bulb (the task editor, which every task list and the edit
 * popup use, and the note form's edit uses) so each does not repeat the three ways this can go
 * wrong: no connection, the AI said no, or the AI could not answer. All three leave the form open
 * with what was typed in it. On success the editor holds the result on screen (`useAiHold`).
 */

import type { RetryMessage } from "./inboxAiRetryMessage";
import {
  ApiError,
  api,
  type ContextEntry,
  type ContextRefineBody,
  type Note,
  type NoteRefineBody,
  type Project,
  type ProjectRefineBody,
  type Task,
  type TaskRefineBody,
} from "./api";

const OFFLINE = "Asking the AI needs a connection. Try again once you're back online.";

export type AiRefined<T> = {
  item: T;
  /** What the AI did, as the bulb's answer box says it (`InboxAiRetryButton`'s `describe`). */
  message: RetryMessage;
};

const FIELD_NAMES: Record<string, string> = {
  title: "title",
  notes: "notes",
  body: "text",
  energy: "energy",
  due: "due date",
  starred: "star",
  important: "importance",
  estimate_min: "estimate",
  area_id: "area",
  project_id: "project",
  name: "name",
  scope_type: "scope",
  scope_id: "scope",
};

function listOf(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "The AI updated the title, notes and star. Why: …" - or that it had nothing to change. */
export function describeAiChange(changed: string[], reason: string | null | undefined): RetryMessage {
  const names = [...new Set(changed.map((field) => FIELD_NAMES[field] ?? field))];
  const what = names.length
    ? `The AI updated the ${listOf(names)}.`
    : "The AI looked it over and had nothing to change.";
  return {
    segments: [{ text: what, strong: true }, ...(reason ? [{ text: ` Why: ${reason}` }] : [])],
    tone: "good",
    filed: true,
  };
}

/** The bulb's answer for an edit: it is already saved, so the answer is the good news. */
export const describeRefined = <T>(refined: AiRefined<T>): RetryMessage => refined.message;

async function run<T extends { ai_reasoning?: { reason: string | null } | null }>(
  call: () => Promise<{
    outcome: "updated" | "failed";
    error: string | null;
    item: T | null;
    changed: string[];
  }>,
): Promise<AiRefined<T>> {
  let answer;
  try {
    answer = await call();
  } catch (err) {
    if (err instanceof ApiError && err.isOffline) throw new Error(OFFLINE);
    throw err;
  }
  if (answer.outcome !== "updated" || !answer.item) {
    throw new Error(answer.error ?? "The AI didn't answer.");
  }
  return {
    item: answer.item,
    message: describeAiChange(answer.changed, answer.item.ai_reasoning?.reason),
  };
}

export const refineTaskInPlace = (id: string, body: TaskRefineBody): Promise<AiRefined<Task>> =>
  run(() => api.refineTask(id, body));

export const refineNoteInPlace = (id: string, body: NoteRefineBody): Promise<AiRefined<Note>> =>
  run(() => api.refineNote(id, body));

export const refineProjectInPlace = (
  id: string,
  body: ProjectRefineBody,
): Promise<AiRefined<Project>> => run(() => api.refineProject(id, body));

export const refineContextInPlace = (
  id: string,
  body: ContextRefineBody,
): Promise<AiRefined<ContextEntry>> => run(() => api.refineContext(id, body));
